/** The quick actions every value on a feature-flag surface offers — the grid
 *  (which IS the editor), the validate dialog and the reseller-policy report.
 *
 *  Two grid facts shape everything here. The flags grid declares `iexact` only
 *  for `tenant` / `ota` / `updated_by` (an `exact` chip could not round-trip
 *  through the `=` spelling), so those are matched by code / name / email and
 *  an all-digits value falls back to the id server-side; `tour`,
 *  `notification` and `id` are exact-only integers. And `tag` is `icontains`
 *  over the stored CHIP TEXT — there is no tag-id namespace, so a tag is found
 *  by name and a same-named tag elsewhere matches too.
 *
 *  A flag row has no detail page: `?q=id=<n>` IS how you deep-link one, which
 *  is what the validate dialog and the policy report hand back.
 */
import type { FeatureFlagListItem, FlagTagEntry, UserRef } from '@/api/notificationFeatures';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  bookingsHref,
  cond,
  deliveryLogsHref,
  featureFlagsHref,
  idCond,
  notificationsHref,
  tagsHref,
} from '@/utils/gridLinks';

/** Feature codename: every other row of the same feature, at every level. */
export function featureActions(codename: string): ValueAction[] {
  return [
    act.filter(featureFlagsHref([cond('feature', codename)]), 'Every flag row for this feature'),
    act.copy(codename, 'Copy feature codename'),
  ];
}

export function levelActions(level: string): ValueAction[] {
  return [act.filter(featureFlagsHref([cond('level', level)]), `Flag rows at the ${level} level`)];
}

export function enabledActions(enabled: boolean): ValueAction[] {
  const value = enabled ? 'yes' : 'no';
  return [
    act.filter(
      featureFlagsHref([cond('enabled', value)]),
      enabled ? 'Every enabled flag row' : 'Every disabled flag row',
    ),
  ];
}

/** An operator a flag row is scoped to. Matched by CODE: the grid's `tenant`
 *  namespace is iexact-only (an all-digits value falls back to the id). */
export function flagTenantActions(tenant: {
  id: number;
  code?: string | null;
  name?: string | null;
}): ValueAction[] {
  const code = tenant.code || tenant.name || String(tenant.id);
  return [
    act.open(`/tour-operators/${tenant.id}`, 'Open operator'),
    act.filter(featureFlagsHref([cond('tenant', code)]), 'Flag rows for this operator'),
    act.copy(code, 'Copy operator code'),
  ];
}

/** The scope a row applies to — polymorphic on its level. The cell already
 *  links the record; these are the sibling rows and the grids that scope
 *  reaches. Returns `[]` for a level with no scope (system / tag). */
export function scopeActions(row: FeatureFlagListItem): ValueAction[] {
  switch (row.level) {
    case 'tenant':
      return row.tenant ? flagTenantActions(row.tenant) : [];
    case 'ota': {
      if (!row.ota) return [];
      const name = row.ota.name ?? String(row.ota.id);
      return [
        act.open(`/otas/${row.ota.id}`, 'Open OTA'),
        act.filter(featureFlagsHref([cond('ota', name)]), 'Flag rows for this OTA'),
      ];
    }
    case 'tour': {
      if (!row.tour) return [];
      const id = String(row.tour.id);
      return [
        act.open(`/tours/${row.tour.id}`, 'Open tour'),
        act.filter(featureFlagsHref([cond('tour', id, 'exact')]), 'Flag rows for this tour'),
        act.filter(
          notificationsHref([cond('tour', id, 'exact')]),
          'Notifications targeting this tour',
        ),
        act.filter(bookingsHref([idCond('tour', row.tour.id)]), 'Bookings on this tour'),
      ];
    }
    case 'notification': {
      if (!row.notification) return [];
      const id = String(row.notification.id);
      return [
        act.open(`/notifications/${row.notification.id}`, 'Open notification'),
        act.filter(
          featureFlagsHref([cond('notification', id, 'exact')]),
          'Flag rows for this notification',
        ),
        act.filter(
          deliveryLogsHref([cond('notification', id, 'exact')], { tenant: row.tenant?.id ?? null }),
          'Delivery logs for this notification (7 days)',
        ),
      ];
    }
    default:
      return [];
  }
}

/** One chip of a level=tag condition. Read from the ROW's `tags`, not from the
 *  widget value: `fromWireChips` keeps only `{tag_id}` for a pinned chip, and
 *  the category page is worth linking too. */
export function flagTagChipActions(
  entry: FlagTagEntry,
  ctx: { tenantId?: number | null } = {},
): ValueAction[] {
  const tenant = ctx.tenantId ?? null;
  if ('tag' in entry) {
    const t = entry.tag;
    return [
      act.open(`/tags/${t.id}`, 'Open tag'),
      act.open(`/tag-categories/${t.category.id}`, 'Open category'),
      act.filter(bookingsHref([idCond('tag', t.id)], t.tenant?.id ?? tenant), 'Bookings with this tag'),
      act.filter(featureFlagsHref([cond('tag', t.name, 'icontains')]), 'Flag rows mentioning this tag'),
      act.copy(t.name, 'Copy tag name'),
    ];
  }
  // By-code chip: it names a tag in whichever tenant is delivering, so the
  // honest target is the tag list filtered by that category code + name.
  return [
    act.filter(
      tagsHref([cond('category', entry.category_code), cond('name', entry.tag_name)], tenant),
      'Tags matching this chip',
    ),
    act.filter(
      featureFlagsHref([cond('tag', entry.tag_name, 'icontains')]),
      'Flag rows mentioning this tag',
    ),
    act.copy(entry.tag_name, 'Copy tag name'),
  ];
}

export function updatedByActions(user: UserRef): ValueAction[] {
  const out: ValueAction[] = [act.open(`/users/${user.id}`, 'Open user')];
  if (user.email) {
    out.push(
      act.filter(featureFlagsHref([cond('updated_by', user.email)]), 'Flag rows this user last changed'),
      act.mailto(user.email),
      act.copy(user.email, 'Copy email'),
    );
  }
  return out;
}

/** created / updated: the ±delta presets, on the flags grid itself. */
export function flagTimestampActions(ns: 'created' | 'updated', iso: string): ValueAction[] {
  return act.timeRanges(iso, (range) => featureFlagsHref([range]), ns);
}

/** A flag ROW — no detail page exists, so `?q=id=` is the deep link. */
export function flagRowActions(rowId: number): ValueAction[] {
  return [
    act.filter(featureFlagsHref([cond('id', String(rowId), 'exact')]), 'Open this row in the grid'),
    act.copy(String(rowId), 'Copy row id'),
  ];
}

/** A row the reseller-policy compiler wrote. A feature flag is reachable by
 *  `?q=id=`; a blacklist rule is NOT — that grid's filters are component state
 *  and it has no `?edit=` intent — so its id is copyable and nothing more. */
export function policyOutcomeActions(outcome: {
  kind: 'feature_flag' | 'blacklist_rule';
  row_id: number | null;
}): ValueAction[] {
  if (outcome.row_id === null) return [];
  if (outcome.kind === 'feature_flag') return flagRowActions(outcome.row_id);
  return [act.copy(String(outcome.row_id), 'Copy blacklist rule id')];
}

/** A reseller tag named by a compiled policy row. */
export function policyTagActions(
  tag: { id: number; name: string },
  tenantId?: number | null,
): ValueAction[] {
  return [
    act.open(`/tags/${tag.id}`, 'Open tag'),
    act.filter(bookingsHref([idCond('tag', tag.id)], tenantId ?? null), 'Bookings with this tag'),
    act.filter(
      featureFlagsHref([cond('tag', tag.name, 'icontains')]),
      'Flag rows mentioning this tag',
    ),
  ];
}
